import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export interface ReportSchedule {
  id: string;
  savedReportId: string;
  cronExpression: string;
  timezone: string;
  recipients: string[];
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastRunStatus: "sent" | "retrying" | "dead" | null;
  lastRunAttempts: number;
  lastRunError: string | null;
}

/**
 * Task 8 (S-2) — CRUD + pause for a saved report's schedules.
 * Mirrors useSavedReports.ts's shape/naming (save→create, refetch, non-fatal
 * mount-fetch). Only fetchSchedules swallows errors on mount; create/update/
 * pause/remove propagate so the caller can show inline validation (the API's
 * 400s for bad cron/recipients are the source of the message — see
 * apps/api/src/routes/report-schedules/index.ts).
 */
export function useReportSchedules(savedReportId: string | null) {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSchedules = useCallback(async () => {
    if (!savedReportId) return;
    setIsLoading(true);
    try {
      const result = await api.get<{ schedules: ReportSchedule[] }>(
        `/analytics/saved-reports/${savedReportId}/schedules`,
      );
      setSchedules(result.schedules);
    } catch {
      // Non-fatal on mount — mirrors useSavedReports.fetchReports.
    } finally {
      setIsLoading(false);
    }
  }, [savedReportId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Shared by update()/pause() — both replace one row in place with the
  // server's fresh copy after a PUT or PATCH.
  const replaceInList = (updated: ReportSchedule) => {
    setSchedules((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const create = async (cronExpression: string, timezone: string, recipients: string[]) => {
    const schedule = await api.post<ReportSchedule>(
      `/analytics/saved-reports/${savedReportId}/schedules`,
      { cronExpression, timezone, recipients },
    );
    setSchedules((prev) => [...prev, schedule]);
    return schedule;
  };

  const update = async (
    id: string,
    data: Partial<{ cronExpression: string; timezone: string; recipients: string[] }>,
  ) => {
    const updated = await api.put<ReportSchedule>(
      `/analytics/saved-reports/${savedReportId}/schedules/${id}`,
      data,
    );
    replaceInList(updated);
    return updated;
  };

  const pause = async (id: string, isActive: boolean) => {
    const updated = await api.patch<ReportSchedule>(
      `/analytics/saved-reports/${savedReportId}/schedules/${id}`,
      { isActive },
    );
    replaceInList(updated);
    return updated;
  };

  const remove = async (id: string) => {
    await api.delete(`/analytics/saved-reports/${savedReportId}/schedules/${id}`);
    setSchedules((prev) => prev.filter((s) => s.id !== id));
  };

  return { schedules, isLoading, create, update, pause, remove, refetch: fetchSchedules };
}
